import isEqual from 'lodash/isEqual';
import Day from '../communication/types/order/Day';
/*
 * The item exposed by the filtered portfolio
 * e.g. for a Blotter this will be an Order
 * (todo for a watchlist...)
 *
 * Note: All we have in here at the moment is those properties that may be required by Portfolio Returns
 * When we do Portfolio Summary (Histogram) we will add more possibly required cols...
 *
 * The "symbol" is the mapped property based on the global Symbology settings
 */

export type PortfolioItem = DefaultPortfolioItem &
    Partial<OptionalPortfolioItem>;

export type PortfolioItemColumn = keyof PortfolioItem;

export interface DefaultPortfolioItem {
    /*
     * Mapped from bloom, cusip, isymbol, localSymC or sedol depending on symbology
     */
    symbol: string;
    instinetSymbol: string;
    side: 'B' | 'S' | 'SS';
    qty: number;
    qtyTraded: number;
    qtyRemaining: number;
    previousNightClose: number;
    averagePrice: number;
    movement: number;
    estCost: number;
}

export const initialPortfolioItem: DefaultPortfolioItem = {
    symbol: '',
    instinetSymbol: '',
    side: 'B',
    qty: 0,
    qtyTraded: 0,
    qtyRemaining: 0,
    previousNightClose: 0,
    averagePrice: 0,
    movement: 0,
    estCost: 0,
};

export const defaultPortfolioItemColumns = Object.keys(
    initialPortfolioItem
) as PortfolioItemColumn[];
/*
 * These are the fields that are only added if requested by consuming widgets such as Portfolio Summary
 */
export interface OptionalPortfolioItem {
    acct: string;
    advPct: number;
    arrivalPSlip: number;
    basket: string;
    brokerName: string;
    client: string;
    clientId: string;
    clusterId: number;
    clusterDescription: string;
    core: string;
    country: string;
    curcy: string;
    dcat: Day;
    desk: string;
    estCost: number;
    ex: string;
    handler: string;
    ind: string;
    idxEvtTrdCat: string;
    liq: number;
    lp: number;
    mcap: number;
    mic: string;
    mktDataMic: string;
    name: string;
    orderInfo: string;
    ownCat: string;
    owner: string;
    reg: string;
    subInd: string;
    secType: string;
    sector: string;
    state: string;
    symbolIndex: string;
    svol: number;
    t0: string;
    t1: string;
    t2: string;
    t3: string;
    t4: string;
    t5: string;
    t6: string;
    t7: string;
    t8: string;
    t9: string;
    t10: string;
    t11: string;
    t12: string;
    t13: string;
    t14: string;
    t15: string;
    t16: string;
    t17: string;
    t18: string;
    t19: string;
    t20: string;
    t21: string;
    t22: string;
    t23: string;
    t24: string;
    wheelStrategy: string;
}

const emptyOptionalPortfolioItem: OptionalPortfolioItem = {
    acct: '',
    advPct: 0,
    arrivalPSlip: 0,
    basket: '',
    brokerName: '',
    client: '',
    clientId: '',
    clusterId: 0,
    clusterDescription: '',
    core: '',
    country: '',
    curcy: '',
    dcat: Day.FRIDAY,
    desk: '',
    estCost: 0,
    ex: '',
    handler: '',
    ind: '',
    idxEvtTrdCat: '',
    liq: 0,
    lp: 0,
    mcap: 0,
    mic: '',
    mktDataMic: '',
    name: '',
    orderInfo: '',
    ownCat: '',
    owner: '',
    reg: '',
    subInd: '',
    secType: '',
    sector: '',
    state: '',
    svol: 0,
    symbolIndex: '',
    t0: '',
    t1: '',
    t2: '',
    t3: '',
    t4: '',
    t5: '',
    t6: '',
    t7: '',
    t8: '',
    t9: '',
    t10: '',
    t11: '',
    t12: '',
    t13: '',
    t14: '',
    t15: '',
    t16: '',
    t17: '',
    t18: '',
    t19: '',
    t20: '',
    t21: '',
    t22: '',
    t23: '',
    t24: '',
    wheelStrategy: '',
};

export const optionalPortfolioItemColumns = Object.keys(
    emptyOptionalPortfolioItem
) as PortfolioItemColumn[];

export function portfolioItemsAreEqual(
    a: PortfolioItem,
    b: PortfolioItem
): boolean {
    return isEqual(a, b);
}

export const portfolioItemOrderValues = (
    item: PortfolioItem,
    factor: 1 | -1 = 1
) => {
    const tradedValue = item.qtyTraded * item.averagePrice * factor;
    const remainingValue = item.qtyRemaining * item.previousNightClose * factor;
    const orderedValue = tradedValue + remainingValue;
    return { tradedValue, remainingValue, orderedValue };
};
